import {
  AddedToGroupNotificationFields,
  BroadcastFieldsForNotification,
  CampaignFields,
  CampaignSponsorAddNotificationFields,
  ChangedGroupPrivacyNotificationFields,
  FollowedByUserNotificationFields,
  GroupFields,
  InvitedToGroupAsGuestNotificationFields,
  MarkedBestReplyFollowedThreadNotificationFields,
  MarkedBestReplyMyReplyNotificationFields,
  MarkedBestReplyMyThreadNotificationFields,
  MessageFields,
  NetworkFields,
  NotificationDetailsField,
  NotificationFields,
  NotificationTypeNames,
  PollSummaryNotificationFields,
  PostedMessageOnBehalfOfUserNotificationFields,
  ReactionNotificationFields,
  RequestedGroupJoinNotificationFields,
  StartedBroadcastEventNotificationFields,
  TeamsMeetingFields,
  TeamsMeetingModeratedThreadPublishedNotificationFields,
  TeamsMeetingStartingNotificationFields,
  TeamsMeetingThreadCreatedNotificationFields,
  ThreadFieldsForNotification,
  ThreadFieldsWithThreadScopeForNotification,
  ThreadFieldsWithWallOwnerForNotification,
  UpvotedQuestionReplyNotificationFields,
  UpvotedQuestionThreadNotificationFields,
  UserFields,
  UserMomentMessagePostNotificationFields,
  UserWallMessagePostNotificationFields,
} from '@ms/yammer-graphql';

import {
  AddedToGroupNotificationDetails,
  CampaignSponsorAddNotificationDetails,
  ChangedGroupPrivacyNotificationDetails,
  FollowedByUserNotificationDetails,
  InvitedToGroupAsGuestNotificationDetails,
  MarkedBestReplyNotificationDetails,
  Notification,
  NotificationSubtype,
  PollSummaryNotificationDetails,
  PostedMessageOnBehalfOfUserNotificationDetails,
  ReactionNotificationDetails,
  RequestedGroupJoinNotificationDetails,
  StartedBroadcastEventNotificationDetails,
  TeamsMeetingModeratedThreadPublishedNotificationDetails,
  TeamsMeetingStartingNotificationDetails,
  TeamsMeetingThreadCreatedNotificationDetails,
  UpvotedQuestionReplyNotificationDetails,
  UpvotedQuestionThreadNotificationDetails,
  UserMomentMessagePostNotificationDetails,
  UserWallMessagePostNotificationDetails,
} from '../../../state/types';
import { Override } from '../../types';

import { PreNormalizedThreadScope, processThreadScope } from './threadScopes';

interface PreNormalizedReactionNotificationDetailsTypesOverrides {
  readonly featuredReactionUserIds: UserFields[];
  readonly groupId?: GroupFields;
  readonly groupTelemetryId?: GroupFields['telemetryId'];
  readonly threadId?: ThreadFieldsWithWallOwnerForNotification['id'];
  readonly threadTelemetryId?: ThreadFieldsWithWallOwnerForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
  readonly isThreadStarter: boolean;
  readonly scope: PreNormalizedThreadScope;
}

type PreNormalizedReactionNotificationDetails = Override<
  ReactionNotificationDetails,
  PreNormalizedReactionNotificationDetailsTypesOverrides
>;

interface PreNormalizedPostedMessageOnBehalfOfUserNotificationDetailsTypesOverrides {
  readonly postingUserId: UserFields;
  readonly groupId?: GroupFields;
  readonly onBehalfOfUserId: UserFields;
  readonly viewerIsOnBehalfOfSender: boolean;
  readonly threadId: ThreadFieldsForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
}

type PreNormalizedPostedMessageOnBehalfOfUserNotificationDetails = Override<
  PostedMessageOnBehalfOfUserNotificationDetails,
  PreNormalizedPostedMessageOnBehalfOfUserNotificationDetailsTypesOverrides
>;

interface PreNormalizedUserWallMessagePostNotificationDetailsTypesOverrides {
  readonly threadId: ThreadFieldsForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
}

type PreNormalizedUserWallMessagePostNotificationDetails = Override<
  UserWallMessagePostNotificationDetails,
  PreNormalizedUserWallMessagePostNotificationDetailsTypesOverrides
>;

interface PreNormalizedUserMomentMessagePostNotificationDetailsTypesOverrides {
  readonly threadId: ThreadFieldsForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
}

type PreNormalizedUserMomentMessagePostNotificationDetails = Override<
  UserMomentMessagePostNotificationDetails,
  PreNormalizedUserMomentMessagePostNotificationDetailsTypesOverrides
>;

interface PreNormalizedMarkedBestReplyNotificationDetailsTypesOverrides {
  readonly markingUserId: UserFields;
  readonly groupId?: GroupFields;
  readonly groupTelemetryId?: GroupFields['telemetryId'];
  readonly threadId: ThreadFieldsWithWallOwnerForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsWithWallOwnerForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
  readonly wallOwnerName?: UserFields['displayName'];
  readonly scope: PreNormalizedThreadScope;
}

type PreNormalizedMarkedBestReplyNotificationDetails = Override<
  MarkedBestReplyNotificationDetails,
  PreNormalizedMarkedBestReplyNotificationDetailsTypesOverrides
>;

interface PreNormalizedStartedBroadcastEventNotificationOverrides {
  readonly groupId: GroupFields;
  readonly groupTelemetryId: GroupFields['telemetryId'];
  readonly broadcastId: BroadcastFieldsForNotification;
}

type PreNormalizedStartedBroadcastEventNotificationDetails = Override<
  StartedBroadcastEventNotificationDetails,
  PreNormalizedStartedBroadcastEventNotificationOverrides
>;

interface PreNormalizedRequestedGroupJoinNotificationOverrides {
  readonly groupId: GroupFields;
  readonly groupTelemetryId: GroupFields['telemetryId'];
  readonly requestingUserId: UserFields;
}

type PreNormalizedRequestedGroupJoinNotificationDetails = Override<
  RequestedGroupJoinNotificationDetails,
  PreNormalizedRequestedGroupJoinNotificationOverrides
>;

interface PreNormalizedAddedToGroupNotificationOverrides {
  readonly groupId: GroupFields;
  readonly groupTelemetryId: GroupFields['telemetryId'];
  readonly addingUserId?: UserFields;
}

type PreNormalizedAddedToGroupNotificationDetails = Override<
  AddedToGroupNotificationDetails,
  PreNormalizedAddedToGroupNotificationOverrides
>;

interface PreNormalizedCampaignSponsorAddNotificationOverrides {
  readonly campaignId: CampaignFields['id'];
  readonly addingUserId: UserFields;
}

type PreNormalizedCampaignSponsorAddNotificationDetails = Override<
  CampaignSponsorAddNotificationDetails,
  PreNormalizedCampaignSponsorAddNotificationOverrides
>;

interface PreNormalizedInvitedToGroupAsGuestNotificationOverrides {
  readonly groupId: GroupFields['id'];
  readonly groupTelemetryId: GroupFields['telemetryId'];
  readonly networkId: NetworkFields['id'];
}

type PreNormalizedInvitedToGroupAsGuestNotificationDetails = Override<
  InvitedToGroupAsGuestNotificationDetails,
  PreNormalizedInvitedToGroupAsGuestNotificationOverrides
>;

interface PreNormalizedChangedGroupPrivacyNotificationOverrides {
  readonly groupId: GroupFields;
  readonly groupTelemetryId: GroupFields['telemetryId'];
  readonly userId: UserFields;
}

type PreNormalizedChangedGroupPrivacyNotificationDetails = Override<
  ChangedGroupPrivacyNotificationDetails,
  PreNormalizedChangedGroupPrivacyNotificationOverrides
>;

interface PreNormalizedFollowedByUserNotificationOverrides {
  readonly featuredFollowerUserIds: UserFields[];
  readonly viewerIsFollowingUsers: UserFields[];
}

type PreNormalizedFollowedByUserNotificationDetails = Override<
  FollowedByUserNotificationDetails,
  PreNormalizedFollowedByUserNotificationOverrides
>;

interface PreNormalizedUpvotedQuestionReplyNotificationDetailsOverrides {
  readonly featuredUpvotingUserIds: UserFields[];
  readonly threadId: ThreadFieldsWithWallOwnerForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsWithWallOwnerForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly groupId: ThreadFieldsWithWallOwnerForNotification['group'];
  readonly wallOwnerName?: UserFields['displayName'];
  readonly wallOwnerId?: UserFields['id'];
  readonly scope: PreNormalizedThreadScope;
}

type PreNormalizedUpvotedQuestionReplyNotificationDetails = Override<
  UpvotedQuestionReplyNotificationDetails,
  PreNormalizedUpvotedQuestionReplyNotificationDetailsOverrides
>;

interface PreNormalizedPollSummaryNotificationOverrides {
  readonly threadId: ThreadFieldsWithWallOwnerForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsWithWallOwnerForNotification['telemetryId'];
  readonly groupId?: ThreadFieldsWithWallOwnerForNotification['group'];
  readonly wallOwnerId?: ThreadFieldsWithWallOwnerForNotification['wallOwner'];
}

type PreNormalizedPollSummaryNotificationDetails = Override<
  PollSummaryNotificationDetails,
  PreNormalizedPollSummaryNotificationOverrides
>;

interface PreNormalizedTeamsMeetingThreadCreatedNotificationOverrides {
  readonly threadId: ThreadFieldsWithThreadScopeForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsWithThreadScopeForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
  readonly scope: PreNormalizedThreadScope;
}

type PreNormalizedTeamsMeetingThreadCreatedNotificationDetails = Override<
  TeamsMeetingThreadCreatedNotificationDetails,
  PreNormalizedTeamsMeetingThreadCreatedNotificationOverrides
>;

interface PreNormalizedTeamsMeetingModeratedThreadPublishedNotificationOverrides {
  readonly threadId: ThreadFieldsWithThreadScopeForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsWithThreadScopeForNotification['telemetryId'];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
  readonly scope: PreNormalizedThreadScope;
}

type PreNormalizedTeamsMeetingModeratedThreadPublishedNotificationDetails = Override<
  TeamsMeetingModeratedThreadPublishedNotificationDetails,
  PreNormalizedTeamsMeetingModeratedThreadPublishedNotificationOverrides
>;

interface PreNormalizedTeamsMeetingStartingNotificationOverrides {
  readonly teamsMeetingId: TeamsMeetingFields;
}

type PreNormalizedTeamsMeetingStartingNotificationDetails = Override<
  TeamsMeetingStartingNotificationDetails,
  PreNormalizedTeamsMeetingStartingNotificationOverrides
>;

interface PreNormalizedUpvotedQuestionThreadNotificationDetailsOverrides {
  readonly featuredUpvotingUserIds: UserFields[];
  readonly messageId: MessageFields;
  readonly messageTelemetryId: MessageFields['telemetryId'];
  readonly threadId: ThreadFieldsWithThreadScopeForNotification['id'];
  readonly threadTelemetryId: ThreadFieldsWithThreadScopeForNotification['telemetryId'];
  readonly scope: PreNormalizedThreadScope;
}

type PreNormalizedUpvotedQuestionThreadNotificationDetails = Override<
  UpvotedQuestionThreadNotificationDetails,
  PreNormalizedUpvotedQuestionThreadNotificationDetailsOverrides
>;

type PreNormalizedDetails =
  | PreNormalizedAddedToGroupNotificationDetails
  | PreNormalizedCampaignSponsorAddNotificationDetails
  | PreNormalizedFollowedByUserNotificationDetails
  | PreNormalizedInvitedToGroupAsGuestNotificationDetails
  | PreNormalizedMarkedBestReplyNotificationDetails
  | PreNormalizedPollSummaryNotificationDetails
  | PreNormalizedPostedMessageOnBehalfOfUserNotificationDetails
  | PreNormalizedReactionNotificationDetails
  | PreNormalizedRequestedGroupJoinNotificationDetails
  | PreNormalizedStartedBroadcastEventNotificationDetails
  | PreNormalizedTeamsMeetingThreadCreatedNotificationDetails
  | PreNormalizedTeamsMeetingModeratedThreadPublishedNotificationDetails
  | PreNormalizedTeamsMeetingStartingNotificationDetails
  | PreNormalizedUpvotedQuestionReplyNotificationDetails
  | PreNormalizedUserMomentMessagePostNotificationDetails
  | PreNormalizedUserWallMessagePostNotificationDetails
  | PreNormalizedUpvotedQuestionThreadNotificationDetails;

export interface PreNormalizedNotificationTypes {
  readonly subtype: NotificationSubtype | null;
  readonly details: PreNormalizedDetails | null;
}

type PreNormalizedNotification = Override<Notification, PreNormalizedNotificationTypes>;

type SupportedTypes = Extract<NotificationTypeNames['__typename'], keyof typeof NotificationSubtype>;
const typenameToSubtype: Record<SupportedTypes, NotificationSubtype> = {
  PostedMessageOnBehalfOfUserNotification: NotificationSubtype.PostedMessageOnBehalfOfUserNotification,
  ReactionNotification: NotificationSubtype.ReactionNotification,
  MarkedBestReplyMyReplyNotification: NotificationSubtype.MarkedBestReplyMyReplyNotification,
  MarkedBestReplyMyThreadNotification: NotificationSubtype.MarkedBestReplyMyThreadNotification,
  MarkedBestReplyFollowedThreadNotification: NotificationSubtype.MarkedBestReplyFollowedThreadNotification,
  StartedBroadcastEventNotification: NotificationSubtype.StartedBroadcastEventNotification,
  RequestedGroupJoinNotification: NotificationSubtype.RequestedGroupJoinNotification,
  AddedToGroupNotification: NotificationSubtype.AddedToGroupNotification,
  InvitedToGroupAsGuestNotification: NotificationSubtype.InvitedToGroupAsGuestNotification,
  ChangedGroupPrivacyNotification: NotificationSubtype.ChangedGroupPrivacyNotification,
  FollowedByUserNotification: NotificationSubtype.FollowedByUserNotification,
  UpvotedQuestionReplyNotification: NotificationSubtype.UpvotedQuestionReplyNotification,
  PollSummaryNotification: NotificationSubtype.PollSummaryNotification,
  UserWallMessagePostNotification: NotificationSubtype.UserWallMessagePostNotification,
  UserMomentMessagePostNotification: NotificationSubtype.UserMomentMessagePostNotification,
  TeamsMeetingThreadCreatedNotification: NotificationSubtype.TeamsMeetingThreadCreatedNotification,
  TeamsMeetingModeratedThreadPublishedNotification:
    NotificationSubtype.TeamsMeetingModeratedThreadPublishedNotification,
  TeamsMeetingStartingNotification: NotificationSubtype.TeamsMeetingStartingNotification,
  CampaignSponsorAddNotification: NotificationSubtype.CampaignSponsorAddNotification,
  UpvotedQuestionThreadNotification: NotificationSubtype.UpvotedQuestionThreadNotification,
};

const isSupportedType = (typename: SupportedTypes | string): typename is SupportedTypes =>
  Object.keys(NotificationSubtype).includes(typename as SupportedTypes);

type ProcessNotification = (value: NotificationFields) => PreNormalizedNotification;
export const processNotification: ProcessNotification = (value) => ({
  id: value.id,
  createdAt: new Date(value.createdAt).getTime(),
  isUnseen: value.isUnseen,
  subtype: isSupportedType(value.details.__typename) ? typenameToSubtype[value.details.__typename] : null,
  details: isSupportedType(value.details.__typename)
    ? typenameToProcessFunc[value.details.__typename](value.details)
    : null,
});

type ProcessReactionNotificationDetails = (
  details: ReactionNotificationFields
) => PreNormalizedReactionNotificationDetails;
const processReactionNotificationDetails: ProcessReactionNotificationDetails = (details) => ({
  featuredReactionUserIds: details.featuredReactionUsers,
  groupId: details.thread.group || undefined,
  groupTelemetryId: details.thread.group?.telemetryId || undefined,
  reactingUsersCount: details.reactingUsersCount,
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  messageId: details.messages[0],
  messageTelemetryId: details.messages[0].telemetryId,
  isThreadStarter: details.thread.threadStarter.id === details.messages[0].id,
  scope: processThreadScope(details.thread.scope),
});

type ProcessPostedMessageOnBehalfOfUserNotificationDetails = (
  details: PostedMessageOnBehalfOfUserNotificationFields
) => PreNormalizedPostedMessageOnBehalfOfUserNotificationDetails;
const processPostedMessageOnBehalfOfUserNotificationDetails: ProcessPostedMessageOnBehalfOfUserNotificationDetails = (
  details
) => ({
  postingUserId: details.postingUser,
  onBehalfOfUserId: details.onBehalfOfUser,
  viewerIsOnBehalfOfSender: details.viewerIsOnBehalfOfUser,
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  groupId: details.thread.group || undefined,
  messageId: details.message,
  messageTelemetryId: details.message.telemetryId,
});

type MarkedBestReplyNotificationFields =
  | MarkedBestReplyFollowedThreadNotificationFields
  | MarkedBestReplyMyReplyNotificationFields
  | MarkedBestReplyMyThreadNotificationFields;
type ProcessMarkedBestReplyNotificationDetails = (
  details: MarkedBestReplyNotificationFields
) => PreNormalizedMarkedBestReplyNotificationDetails;
const processMarkedBestReplyNotificationDetails: ProcessMarkedBestReplyNotificationDetails = (details) => ({
  markingUserId: details.markingUser,
  groupId: details.thread.group || undefined,
  groupTelemetryId: details.thread.group?.telemetryId || undefined,
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  messageId: details.messages[0],
  messageTelemetryId: details.messages[0].telemetryId,
  wallOwnerName: details.thread.wallOwner?.displayName || undefined,
  scope: processThreadScope(details.thread.scope),
});

type ProcessStartedBroadcastEventNotificationDetails = (
  details: StartedBroadcastEventNotificationFields
) => PreNormalizedStartedBroadcastEventNotificationDetails;
const processStartedBroadcastEventNotificationDetails: ProcessStartedBroadcastEventNotificationDetails = (details) => ({
  groupId: details.broadcast.group,
  groupTelemetryId: details.broadcast.group.telemetryId,
  broadcastId: details.broadcast,
});

type ProcessRequestedGroupJoinNotificationDetails = (
  details: RequestedGroupJoinNotificationFields
) => PreNormalizedRequestedGroupJoinNotificationDetails;
const processRequestedGroupJoinNotificationDetails: ProcessRequestedGroupJoinNotificationDetails = (details) => ({
  groupId: details.group,
  groupTelemetryId: details.group.telemetryId,
  requestingUserId: details.requestingUser,
  isResolved: details.isResolved,
});

type ProcessAddedToGroupNotificationDetails = (
  details: AddedToGroupNotificationFields
) => PreNormalizedAddedToGroupNotificationDetails;
const processAddedToGroupNotificationDetails: ProcessAddedToGroupNotificationDetails = (details) => ({
  groupId: details.group,
  groupTelemetryId: details.group.telemetryId,
  addingUserId: details.addingUser ?? undefined,
});

type ProcessCampaignSponsorAddNotificationDetails = (
  details: CampaignSponsorAddNotificationFields
) => PreNormalizedCampaignSponsorAddNotificationDetails;
const processCampaignSponsorAddNotificationDetails: ProcessCampaignSponsorAddNotificationDetails = (details) => ({
  campaignId: details.campaign.id,
  addingUserId: details.addedByUser,
});

type ProcessInvitedToGroupAsGuestNotificationDetails = (
  details: InvitedToGroupAsGuestNotificationFields
) => PreNormalizedInvitedToGroupAsGuestNotificationDetails;
const processInvitedToGroupAsGuestNotificationDetails: ProcessInvitedToGroupAsGuestNotificationDetails = (details) => ({
  groupId: details.groupId,
  groupTelemetryId: details.groupId,
  networkId: details.networkId,
});

type ProcessChangedGroupPrivacyNotificationDetails = (
  details: ChangedGroupPrivacyNotificationFields
) => PreNormalizedChangedGroupPrivacyNotificationDetails;
const processChangedGroupPrivacyNotificationDetails: ProcessChangedGroupPrivacyNotificationDetails = (details) => ({
  groupId: details.group,
  groupTelemetryId: details.group.telemetryId,
  userId: details.user,
  fromPrivacy: details.fromPrivacy,
  toPrivacy: details.toPrivacy,
});

type ProcessFollowedByUserNotification = (
  details: FollowedByUserNotificationFields
) => PreNormalizedFollowedByUserNotificationDetails;
const processFollowedByUserNotification: ProcessFollowedByUserNotification = (details) => ({
  featuredFollowerUserIds: details.featuredFollowerUsers,
  viewerIsFollowingUsers: details.featuredFollowerUsers,
  followingUsersCount: details.followingUsersCount,
});

type ProcessUpvotedQuestionReplyNotification = (
  details: UpvotedQuestionReplyNotificationFields
) => PreNormalizedUpvotedQuestionReplyNotificationDetails;
const processUpvotedQuestionReplyNotification: ProcessUpvotedQuestionReplyNotification = (details) => ({
  featuredUpvotingUserIds: details.featuredUpvotingUsers,
  upvotingUsersCount: details.upvotingUsersCount,
  messageId: details.message,
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  groupId: details.thread.group,
  wallOwnerName: details.thread.wallOwner?.displayName || undefined,
  wallOwnerId: details.thread.wallOwner?.id,
  scope: processThreadScope(details.thread.scope),
});

type ProcessPollSummaryNotification = (
  details: PollSummaryNotificationFields
) => PreNormalizedPollSummaryNotificationDetails;
const processPollSummaryNotification: ProcessPollSummaryNotification = (details) => ({
  votingUsersCount: details.votingUsersCount,
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  groupId: details.thread.group || undefined,
  wallOwnerId: details.thread.wallOwner || undefined,
});

type ProcessUserWallMessagePostNotification = (
  details: UserWallMessagePostNotificationFields
) => PreNormalizedUserWallMessagePostNotificationDetails;
const processUserWallMessagePostNotification: ProcessUserWallMessagePostNotification = (details) => ({
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  messageId: details.thread.threadStarter,
  messageTelemetryId: details.thread.threadStarter.telemetryId,
  isAnnouncement: details.thread.isAnnouncement,
});

type ProcessUserMomentMessagePostNotification = (
  details: UserMomentMessagePostNotificationFields
) => PreNormalizedUserMomentMessagePostNotificationDetails;
const processUserMomentMessagePostNotification: ProcessUserMomentMessagePostNotification = (details) => ({
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  messageId: details.thread.threadStarter,
  messageTelemetryId: details.thread.threadStarter.telemetryId,
});

type ProcessTeamsMeetingThreadCreatedNotification = (
  details: TeamsMeetingThreadCreatedNotificationFields
) => PreNormalizedTeamsMeetingThreadCreatedNotificationDetails;
const processTeamsMeetingThreadCreatedNotification: ProcessTeamsMeetingThreadCreatedNotification = (details) => ({
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  messageId: details.thread.threadStarter,
  messageTelemetryId: details.thread.threadStarter.telemetryId,
  scope: processThreadScope(details.thread.scope),
});

type ProcessTeamsMeetingModeratedThreadPublishedNotification = (
  details: TeamsMeetingModeratedThreadPublishedNotificationFields
) => PreNormalizedTeamsMeetingModeratedThreadPublishedNotificationDetails;
const processTeamsMeetingModeratedThreadPublishedNotification: ProcessTeamsMeetingModeratedThreadPublishedNotification =
  (details) => ({
    threadId: details.thread.id,
    threadTelemetryId: details.thread.telemetryId,
    messageId: details.thread.threadStarter,
    messageTelemetryId: details.thread.threadStarter.telemetryId,
    scope: processThreadScope(details.thread.scope),
  });

type ProcessTeamsMeetingStartingNotification = (
  details: TeamsMeetingStartingNotificationFields
) => PreNormalizedTeamsMeetingStartingNotificationDetails;
const processTeamsMeetingStartingNotification: ProcessTeamsMeetingStartingNotification = (details) => ({
  teamsMeetingId: details.teamsMeeting,
});

type ProcessUpvotedQuestionThreadNotification = (
  details: UpvotedQuestionThreadNotificationFields
) => PreNormalizedUpvotedQuestionThreadNotificationDetails;
const processUpvotedQuestionThreadNotification: ProcessUpvotedQuestionThreadNotification = (details) => ({
  featuredUpvotingUserIds: details.featuredUpvotingUsers,
  upvotingUsersCount: details.upvotingUsersCount,
  threadId: details.thread.id,
  threadTelemetryId: details.thread.telemetryId,
  scope: processThreadScope(details.thread.scope),
  messageId: details.thread.threadStarter,
  messageTelemetryId: details.thread.threadStarter.telemetryId,
});

const typenameToProcessFunc: Record<SupportedTypes, (details: NotificationDetailsField) => PreNormalizedDetails> = {
  PostedMessageOnBehalfOfUserNotification: processPostedMessageOnBehalfOfUserNotificationDetails,
  ReactionNotification: processReactionNotificationDetails,
  MarkedBestReplyMyReplyNotification: processMarkedBestReplyNotificationDetails,
  MarkedBestReplyMyThreadNotification: processMarkedBestReplyNotificationDetails,
  MarkedBestReplyFollowedThreadNotification: processMarkedBestReplyNotificationDetails,
  StartedBroadcastEventNotification: processStartedBroadcastEventNotificationDetails,
  RequestedGroupJoinNotification: processRequestedGroupJoinNotificationDetails,
  AddedToGroupNotification: processAddedToGroupNotificationDetails,
  InvitedToGroupAsGuestNotification: processInvitedToGroupAsGuestNotificationDetails,
  ChangedGroupPrivacyNotification: processChangedGroupPrivacyNotificationDetails,
  FollowedByUserNotification: processFollowedByUserNotification,
  UpvotedQuestionReplyNotification: processUpvotedQuestionReplyNotification,
  PollSummaryNotification: processPollSummaryNotification,
  UserWallMessagePostNotification: processUserWallMessagePostNotification,
  UserMomentMessagePostNotification: processUserMomentMessagePostNotification,
  TeamsMeetingThreadCreatedNotification: processTeamsMeetingThreadCreatedNotification,
  TeamsMeetingModeratedThreadPublishedNotification: processTeamsMeetingModeratedThreadPublishedNotification,
  TeamsMeetingStartingNotification: processTeamsMeetingStartingNotification,
  CampaignSponsorAddNotification: processCampaignSponsorAddNotificationDetails,
  UpvotedQuestionThreadNotification: processUpvotedQuestionThreadNotification,
};
